<script lang="ts">
	// @ts-nocheck
	import * as Carousel from '$lib/components/ui/carousel/index.js';
	import type { CarouselAPI } from '$lib/components/ui/carousel/context.js';
	import { enhancedImages } from '$lib/utils/images';
	import { onMount } from 'svelte';

	let { tour } = $props();
	let videos = [
		{
			productCode: 'P18ZC5', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/hAkxkStVOQQ' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PJHLE0', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/sOVKn60RX9s' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PQKW4C', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/MqVeiwLNYYY' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PNVP8M', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/FRktDNIX8Mo' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PTC0KJ', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/5E2OmcTbHcs' // Replace with your actual YouTube video ID or embed URL
		}
	];

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;
		
		// Only consider local file paths as potential enhanced images
		// External URLs (containing http:// or https://) are not enhanced
		if (imageUrl.includes('http://') || imageUrl.includes('https://')) {
			return false;
		}
		
		// Try to find the image in our enhanced images collection
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch (err) {
			console.warn('Error checking enhanced image:', err);
			return false;
		}
	}
	
	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;
		
		try {
			// If it's an enhanced image, get it from the enhancedImages collection
			const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (enhancedEntry && enhancedEntry[1] && enhancedEntry[1].default) {
				return enhancedEntry[1].default;
			}
		} catch (err) {
			console.warn('Error getting enhanced image source:', err);
		}
		
		// Otherwise, use the URL directly
		return imageUrl;
	}

	let api = $state<CarouselAPI>();
	const count = $derived(api ? api.scrollSnapList().length : 0);
	let current = $state(0);

	$effect(() => {
		if (api) {
			current = api.selectedScrollSnap() + 1;
			api.on('select', () => {
				current = api!.selectedScrollSnap() + 1;
			});
		}
	});
	
	// Function to restore original image URLs after build
	function restoreOriginalImageUrls() {
		if (!tour || !tour.images) return;
		
		// Check each image for stored original URLs (from server-side)
		tour.images.forEach(img => {
			if (img._originalUrl) {
				// Restore the original URL that was replaced during build
				img.itemUrl = img._originalUrl;
				delete img._originalUrl;
			}
		});
	}
	
	// When component mounts, restore any original image URLs
	onMount(() => {
		restoreOriginalImageUrls();
	});
</script>

<section class="mx-auto flex w-full max-w-7xl flex-col px-4 pb-0 pt-20 sm:pb-12">
	<h2
		class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		Gallery
	</h2>
	{#if videos.some((v) => v.productCode === tour.productCode)}
		<div
			class="mx-auto flex w-full max-w-7xl flex-col gap-4 overflow-hidden py-12 lg:grid lg:grid-cols-4"
		>
			<div class=" col-span-3 aspect-video">
				<Carousel.Root
					setApi={(emblaApi) => (api = emblaApi)}
					class="w-full rounded-3xl"
					opts={{ align: 'start', loop: true }}
				>
					<Carousel.Content class=" rounded-3xl lg:h-[480px]">
						<Carousel.Item class="aspect-video w-full lg:h-[480px]">
							<iframe
								src={videos.find((v) => v.productCode === tour.productCode)?.src}
								title="YouTube video player"
								class="h-full w-full rounded-3xl"
								frameborder="0"
								allowfullscreen
							></iframe>
						</Carousel.Item>
						{#each tour.images as image, index}
							<Carousel.Item class="aspect-video w-full lg:h-[480px]">
								{#if isEnhancedImage(image.itemUrl)}
									<enhanced:img
										src={getImageSource(image.itemUrl)}
										alt={tour.name}
										class="h-full w-full rounded-3xl object-cover"
										sizes="(min-width: 1024px) 1024px, 100vw"
										loading="lazy"
									/>
								{:else}
									<img
										src={image.itemUrl}
										alt={tour.name || 'Tour Image'}
										class="h-full w-full rounded-3xl object-cover"
										loading="lazy"
										onerror={(e) => { 
											console.warn('Gallery image failed to load:', image.itemUrl);
											e.currentTarget.onerror=null; 
											e.currentTarget.src='/images/placeholder-tour.jpg'; 
										}}
									/>
								{/if}
							</Carousel.Item>
						{/each}
					</Carousel.Content>
				</Carousel.Root>
			</div>

			<div class="relative col-span-1 flex w-full gap-4 overflow-auto lg:h-[480px] lg:flex-col">
				{#each tour.images as image, index}
					<button
						aria-label="gallery-select"
						onclick={() => api.scrollTo(index + 1)}
						class="relative w-full p-20 transition"
					>
						{#if isEnhancedImage(image.itemUrl)}
							<enhanced:img
								src={getImageSource(image.itemUrl)}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name}
								sizes="(min-width: 1024px) 25vw, 100px"
								loading="lazy"
							/>
						{:else}
							<img
								src={image.itemUrl}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name || 'Tour Image'}
								loading="lazy"
								onerror={(e) => { 
									console.warn('Gallery thumbnail failed to load:', image.itemUrl);
									e.currentTarget.onerror=null; 
									e.currentTarget.src='/images/placeholder-tour.jpg'; 
								}}
							/>
						{/if}
					</button>
				{/each}
				<button
					aria-label="gallery-select"
					onclick={() => api.scrollTo(0)}
					class="relative w-full p-20 transition"
					><iframe
						src={videos.find((v) => v.productCode === tour.productCode)?.src}
						title="YouTube video player"
						class="pointer-events-none absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
						frameborder="0"
						allowfullscreen
					></iframe></button
				>
			</div>
		</div>
	{:else}
		<div
			class="mx-auto flex w-full max-w-7xl flex-col gap-4 overflow-hidden py-12 lg:grid lg:grid-cols-4"
		>
			<div class=" col-span-3 aspect-video">
				<Carousel.Root
					setApi={(emblaApi) => (api = emblaApi)}
					class="w-full rounded-3xl"
					opts={{ align: 'start', loop: true }}
				>
					<Carousel.Content class=" rounded-3xl lg:h-[480px]">
						{#each tour.images as image, index}
							<Carousel.Item class="aspect-video w-full lg:h-[480px]">
								{#if isEnhancedImage(image.itemUrl)}
									<enhanced:img
										src={getImageSource(image.itemUrl)}
										alt={tour.name}
										class="h-full w-full rounded-3xl object-cover"
										sizes="(min-width: 1024px) 1024px, 100vw"
										loading="lazy"
									/>
								{:else}
									<img
										src={image.itemUrl}
										alt={tour.name || 'Tour Image'}
										class="h-full w-full rounded-3xl object-cover"
										loading="lazy"
										onerror={(e) => { 
											console.warn('Gallery image failed to load:', image.itemUrl);
											e.currentTarget.onerror=null; 
											e.currentTarget.src='/images/placeholder-tour.jpg'; 
										}}
									/>
								{/if}
							</Carousel.Item>
						{/each}
					</Carousel.Content>
				</Carousel.Root>
			</div>
			<div class="relative col-span-1 flex w-full gap-4 overflow-auto lg:h-[480px] lg:flex-col">
				{#each tour.images as image, index}
					<button
						aria-label="gallery-select"
						onclick={() => api.scrollTo(index)}
						class="relative w-full p-20 transition"
						>{#if isEnhancedImage(image.itemUrl)}
							<enhanced:img
								src={getImageSource(image.itemUrl)}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name}
								sizes="(min-width: 1024px) 25vw, 100px"
								loading="lazy"
							/>
						{:else}
							<img
								src={image.itemUrl}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name || 'Tour Image'}
								loading="lazy"
								onerror={(e) => { 
									console.warn('Gallery thumbnail failed to load:', image.itemUrl);
									e.currentTarget.onerror=null; 
									e.currentTarget.src='/images/placeholder-tour.jpg'; 
								}}
							/>
						{/if}
					</button>
				{/each}
			</div>
		</div>
	{/if}
</section>
